import { useEffect, useRef, useContext } from "react";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "socket.io-client";
import useSound from "use-sound";

import { i18n } from "../../translate/i18n";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { subscribeTicketMovedToTop } from "../../helpers/notificationBus";
import { resumoDoVCard } from "../../helpers/vcard";

// Notificação de navegador (Web Notifications API) — componente headless, sem UI.
// Mostra o pop-up do sistema operacional quando chega mensagem nova e o atendente
// NÃO está olhando aquele ticket (aba/janela em segundo plano, ou outro ticket aberto).
//
// Preferências por usuário (persistidas no banco, editáveis no NotificationSettingsModal,
// acionado pelo botão de configuração dentro do dropdown de filtros):
//   - notificacoesDesktop: gate mestre (default ligado).
//   - notificacoesSom: toca um som junto do pop-up (default DESLIGADO).
//   - notificacoesEscopo (só gestor): "todas" | "minhas" | "desabilitado".
//
// Desativável por instância via REACT_APP_NOTIFICACOES_DESKTOP=off (default = ligado).
const notificacoesDesktopAtivas =
	process.env.REACT_APP_NOTIFICACOES_DESKTOP !== "off";

const DesktopNotificationManager = () => {
	const history = useHistory();
	const { user } = useContext(AuthContext);

	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const historyRef = useRef(history);
	// Notificações abertas no momento, indexadas por tag (= ticketId). Permite
	// fechar o pop-up quando o ticket é lido/removido.
	const desktopNotificationsRef = useRef([]);
	// Dedup: a mesma mensagem pode ser publicada por mais de uma lista (Contatos /
	// Grupos), pois ambas escutam o mesmo evento socket. Guarda as últimas chaves
	// `${ticketId}:${messageId}` já notificadas (FIFO, limitado).
	const recentNotifiedRef = useRef({ set: new Set(), order: [] });

	const [playSound] = useSound(alertSound);
	const soundRef = useRef();

	// Pede permissão uma única vez ao montar; mantém o player de som atualizado.
	useEffect(() => {
		soundRef.current = playSound;
		if (!notificacoesDesktopAtivas) return;
		if (!("Notification" in window)) {
			console.log("Este navegador não suporta notificações desktop");
			return;
		}
		if (Notification.permission === "default") {
			Notification.requestPermission();
		}
	}, [playSound]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		historyRef.current = history;
	}, [history]);

	useEffect(() => {
		if (!notificacoesDesktopAtivas) return;
		if (!user || JSON.stringify(user) === "{}") return;

		// Mesma multiplexação de sockets usada no resto do app (um por urlBackend).
		const normalizeUrl = u => (u || "").trim().replace(/\/+$/, "");
		const urlsUnicas = new Set([normalizeUrl(process.env.REACT_APP_BACKEND_URL)]);
		(user.whatsapps || [])
			.map(w => normalizeUrl(w.urlBackend))
			.filter(Boolean)
			.forEach(url => urlsUnicas.add(url));

		const sockets = [];
		[...urlsUnicas].forEach(url => {
			const socket = openSocket(url.trim() + "/");
			sockets.push(socket);

			socket.on("connect", () => socket.emit("joinNotification"));

			socket.on("ticket", data => {
				// Ticket lido ou removido → fecha o pop-up correspondente, se aberto.
				if (data.action === "updateUnread" || data.action === "delete") {
					const idx = desktopNotificationsRef.current.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (idx !== -1) {
						try {
							desktopNotificationsRef.current[idx].close();
						} catch (_) {
							/* noop */
						}
						desktopNotificationsRef.current.splice(idx, 1);
					}
				}
			});
		});

		// A notificação NÃO escuta mais o socket "appMessage" por conta própria.
		// Ela só dispara quando a TicketsList sobe a conversa pro topo da lista
		// (todas as verificações da lista passaram). Aqui mantemos as regras de
		// QUEM notifica (escopo, aceita, ticket visível, etc.).
		const handleTicketMovedToTop = data => {
			if (!data || data.action !== "create" || data?.message?.read) return;

			// Dedup: mesma mensagem pode chegar por mais de uma lista (Contatos/Grupos).
			const key = `${data.ticket?.id}:${data.message?.id}`;
			const dedup = recentNotifiedRef.current;
			if (dedup.set.has(key)) return;
			dedup.set.add(key);
			dedup.order.push(key);
			if (dedup.order.length > 100) {
				dedup.set.delete(dedup.order.shift());
			}

			// Gate mestre por usuário (default ligado).
			if (user?.notificacoesDesktop === false) return;

			const isGestor = !!user?.gestor;
			const escopo = user?.notificacoesEscopo || "minhas";

			// Gestor pode desabilitar tudo pelo escopo.
			if (isGestor && escopo === "desabilitado") return;

			// Só conversas ACEITAS. No backend, tanto a aba "Conversas" quanto
			// "Grupos" são status "open" (a única diferença é isGroup); já a aba
			// "Aguardando" é status "pending". Então status "open" cobre Conversas +
			// Grupos e exclui todo o "Aguardando". Vale inclusive para o escopo
			// "todas" do gestor.
			// Grupo entra sempre: nunca cai em "Aguardando" (nasce e reabre em
			// "open"), e o "|| isGroup" garante grupo antigo que ficou pending
			// antes da limpeza — ele aparece na aba Grupos, então notifica.
			if (data.ticket.status !== "open" && !data.ticket.isGroup) return;

			// Só conexões do usuário. Gestor ("Acesso a todas as conexões") vê todas.
			// (Redundante com o filtro da lista, mas mantido como defesa.)
			if (!isGestor) {
				const userWhatsappIds = (user?.whatsapps || []).map(w => w.id);
				if (!userWhatsappIds.includes(data.ticket.whatsappId)) return;
			}

			// Escopo de quais conversas notificam:
			//  - gestor + "todas": qualquer conversa aceita / grupo;
			//  - demais casos: só as minhas (meu userId) ou grupos.
			const escopoTodas = isGestor && escopo === "todas";
			if (!escopoTodas) {
				const minhaOuGrupo =
					data.ticket.userId === user?.id || data.ticket.isGroup;
				if (!minhaOuGrupo) return;
			}

			// Não notificar o ticket que já está aberto e visível na tela.
			const jaAbertoEVisivel =
				data.message.ticketId === ticketIdRef.current &&
				document.visibilityState === "visible";
			if (jaAbertoEVisivel) return;

			handleNotification(data);
		};

		const unsubscribe = subscribeTicketMovedToTop(handleTicketMovedToTop);

		return () => {
			unsubscribe();
			sockets.forEach(socket => socket.disconnect());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const handleNotification = data => {
		const { message, contact, ticket } = data;

		if (!("Notification" in window) || Notification.permission !== "granted") {
			return;
		}

		const options = {
			body: `${message.mediaType === "vcard" ? resumoDoVCard(message.body) : message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: String(ticket.id),
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		// Mantém referência para conseguir fechar depois (ticket lido/removido).
		const idx = desktopNotificationsRef.current.findIndex(
			n => n.tag === notification.tag
		);
		if (idx !== -1) {
			desktopNotificationsRef.current[idx] = notification;
		} else {
			desktopNotificationsRef.current.unshift(notification);
		}

		// Som opcional (default desligado), só quando habilitado pelo usuário e
		// SOMENTE quando o pop-up está de fato visível para o atendente — ou seja,
		// quando a janela/aba NÃO está em foco (aba em segundo plano ou foco em outra
		// janela). Se o app está em primeiro plano e visível, o SO não exibe o banner,
		// então também não deve tocar som.
		const appEmFoco =
			document.visibilityState === "visible" && document.hasFocus();
		if (user?.notificacoesSom === true && !appEmFoco && soundRef.current) {
			try {
				soundRef.current();
			} catch (_) {
				/* autoplay pode estar bloqueado até interação do usuário */
			}
		}
	};

	return null;
};

export default DesktopNotificationManager;
